import { useEffect, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import { useMutation } from "@apollo/client";

import {
  NavBucketedIdState,
  NavOptedBackInState,
  NavOptOutState,
  NewNavOptInState,
} from "MVVM/State/nav";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import { abTestValue } from "MVVM/Utilities/AbTest";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { useIsInternalAccount } from "MVVM/Hooks/useIsInternalAccount";
import { useUser } from "MVVM/Hooks/useUser";
import { USER_NEW_NAV_OPT_OUT } from "MVVM/GraphQL/mutations/userNewNavOptOut";
import { noticeError } from "MVVM/Utilities/NewRelic";

// Permanent: changing it reshuffles every user mid-experiment.
export const NAV_EXPERIMENT_SALT = "nav-enhanced-v1";
export const NAV_ROLLOUT = 0.5;

// Module scope, not a ref: the param should be consumed once per page load no
// matter how many components call this hook.
let paramApplied = false;

// Module scope too: two callers mounting in the same tick would otherwise both
// fire an exposure event.
let bucketedFor: string | null = null;

export const resetNewNavParamForTests = () => {
  paramApplied = false;
  bucketedFor = null;
};

export const useNewNav = () => {
  const [enabled, setEnabled] = useRecoilState(NewNavOptInState);
  const [optedOut, setOptedOut] = useRecoilState(NavOptOutState);
  const [bucketedId, setBucketedId] = useRecoilState(NavBucketedIdState);
  const [optedBackIn, setOptedBackIn] = useRecoilState(NavOptedBackInState);
  const { track } = useAnalyticsContext();
  const isInternal = useIsInternalAccount();
  const { allstarUser } = useUser();
  const [persistOptOut] = useMutation(USER_NEW_NAV_OPT_OUT);
  const forced = useRef(false);
  // useAnalyticsContext rebuilds track every render; the effect must not.
  const trackRef = useRef(track);
  trackRef.current = track;

  const [anonymousId, setAnonymousId] = useState<string | null>(
    () => window.rudderanalytics?.getAnonymousId?.() ?? null,
  );

  // The rudder snippet queues calls until the SDK loads, so the id is not there
  // on first paint. ready() fires once it is; if rudder never loads, it never
  // fires and the visitor stays in control.
  useEffect(() => {
    if (anonymousId) return;

    window.rudderanalytics?.ready?.(() =>
      setAnonymousId(window.rudderanalytics?.getAnonymousId?.() ?? null),
    );
  }, [anonymousId]);

  useEffect(() => {
    if (paramApplied) return;
    paramApplied = true;

    const param = getQueryParam("newnav");
    if (param !== "1" && param !== "0") return;

    const next = param === "1";
    // Opting in is only for people who were already in the experiment and left.
    // Anyone else self-selecting out of control would bias the result; internal
    // accounts get there through the force effect below instead.
    if (next && !optedOut) return;
    if (next === enabled && !optedOut) return;

    if (next) {
      setOptedOut(false);
      setOptedBackIn(true);
    }
    setEnabled(next);
    track("Nav Variant Changed", {
      nav_variant: next ? "new_nav" : "control",
      source: "query_param",
    });
    // Runs once. Re-running on `enabled` would re-fire the exposure event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Internal accounts are opted in automatically. This writes the flag rather
   * than just returning true, so useAnalyticsContext reports them as new_nav
   * instead of tagging them control while they look at the new bar.
   *
   * ?newnav=0 still wins: without that escape hatch nobody internal could QA
   * the control cohort. Not latched behind a module guard because isInternal is
   * false until the user hydrates.
   */
  useEffect(() => {
    if (forced.current || !isInternal || enabled) return;
    if (getQueryParam("newnav") === "0") return;

    forced.current = true;
    setEnabled(true);
    track("Nav Variant Changed", {
      nav_variant: "new_nav",
      source: "internal_account",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternal, enabled]);

  useEffect(() => {
    if (optedOut || optedBackIn || !allstarUser?.user?.newNavOptOutAt) return;
    if (getQueryParam("newnav") === "1") return;

    setOptedOut(true);
    setEnabled(false);
  }, [
    allstarUser?.user?.newNavOptOutAt,
    optedOut,
    optedBackIn,
    setOptedOut,
    setEnabled,
  ]);

  useEffect(() => {
    if (optedOut || !anonymousId) return;
    if (bucketedId === anonymousId || bucketedFor === anonymousId) return;
    if (getQueryParam("newnav") !== null || isInternal) return;
    if (!window.crypto?.subtle) return;

    bucketedFor = anonymousId;

    abTestValue(anonymousId, NAV_EXPERIMENT_SALT).then((value) => {
      const next = value < NAV_ROLLOUT;

      setBucketedId(anonymousId);
      setEnabled(next);
      trackRef.current("Nav Variant Changed", {
        nav_variant: next ? "new_nav" : "control",
        source: "hash_bucket",
        nav_bucket_value: value,
      });
    });
  }, [
    anonymousId,
    bucketedId,
    optedOut,
    isInternal,
    setBucketedId,
    setEnabled,
  ]);

  const optOut = (source: string) => {
    setOptedOut(true);
    setOptedBackIn(false);
    setEnabled(false);
    track("Nav Opted Out", { nav_variant: "new_nav", source });

    if (!allstarUser?.loggedIn) return;

    persistOptOut({ variables: { input: {} } }).catch(() =>
      noticeError("Nav opt-out failed to persist"),
    );
  };

  // ponytail: kill switch, so ending the experiment does not require clearing
  // every opted-in user's localStorage.
  const newNav =
    enabled && !optedOut && process.env.REACT_APP_NEW_NAV_KILL !== "true";

  const optBackIn = () => {
    setOptedOut(false);
    setOptedBackIn(true);
    setEnabled(true);
    track("Nav Opted Back In", { nav_variant: "new_nav" });
  };

  return { newNav, setNewNav: setEnabled, optOut, optBackIn, optedOut };
};
